import {
  type ApplicationValidationError,
  type ChangesetValidationError,
  type ConfigurationValidationError,
  type DependencyValidationError,
  isTemplateApplicationValidationError,
  type TemplateApplicationValidationError,
  type TemplateDependencyValidationError,
} from '@festina/configuration-general-types/configuration'
import type { LayerKey, LocationErrors } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'

import type { ParameterConfigurationItem } from '../../types/common'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { OperationClassForParameterConfigurationItem } from '../../uiModels/parameterOperations/parameterConfigurationItems'
import {
  isApplicationValidationError,
  isChangesetValidationError,
  isDependencyValidationError,
  isOperationValidationError,
  isTemplateDependencyValidationError,
} from '../../uiModels/types'
import type { ConfigurationItemsByLayer } from '../../workspace/operationsContext'

const isErrorOfParameterConfigurationItem = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
  item: ParameterConfigurationItem,
): validationError is ValidationErrorWithLocation<
  DependencyValidationError | ApplicationValidationError
> =>
  (isDependencyValidationError(validationError.error) ||
    isApplicationValidationError(validationError.error)) &&
  validationError.error.operationClass ===
    OperationClassForParameterConfigurationItem[item]

export const pickNonOperationSpecificErrors = (
  allErrors: ValidationErrorWithLocation<ConfigurationValidationError>[],
) =>
  allErrors?.filter(
    (
      validationError,
    ): validationError is ValidationErrorWithLocation<
      ApplicationValidationError | ChangesetValidationError
    > =>
      (isOperationValidationError(validationError.error) && // TemplateApplicationValidationErrors are shown in the operations list
        (!validationError.error.operationClass ||
          ![
            'TemplateCreateOperation',
            'TemplateHideOperation',
            ...Object.values(OperationClassForParameterConfigurationItem),
            'EnumDefinitionCreateOperation',
            'EnumLiteralCreateOperation',
            'GlMapCreateOperation',
            'GlMapHideOperation',
            'ReconciliationSpecCreateOperation',
          ].includes(validationError.error.operationClass))) ||
      isChangesetValidationError(validationError.error),
  )

export const countTotalErrors = (errors: Optional<LocationErrors[]>) =>
  (errors ?? []).reduce(
    (acc, { errors, serverError }) =>
      acc + (errors?.length ?? 0) + (serverError ? 1 : 0),
    0,
  )

export const filterTemplateErrors = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
): validationError is ValidationErrorWithLocation<
  | TemplateDependencyValidationError
  | TemplateApplicationValidationError
  | ApplicationValidationError
> =>
  isTemplateDependencyValidationError(validationError.error) ||
  isTemplateApplicationValidationError(validationError.error) ||
  (isApplicationValidationError(validationError.error) &&
    validationError.error.operationClass === 'TemplateCreateOperation')

export const filterParameterDefinitionErrors = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
) => isErrorOfParameterConfigurationItem(validationError, 'parameterDefinition')

export const filterParameterValueErrors = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
) => isErrorOfParameterConfigurationItem(validationError, 'parameterValue')

export const filterParameterValueAmendmentErrors = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
) =>
  isErrorOfParameterConfigurationItem(
    validationError,
    'parameterValueAmendment',
  )

export const filterGlMapErrors = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
): validationError is ValidationErrorWithLocation<
  DependencyValidationError | ApplicationValidationError
> =>
  (isDependencyValidationError(validationError.error) ||
    isApplicationValidationError(validationError.error)) &&
  !!validationError.error.operationClass &&
  ['GlMapCreateOperation', 'GlMapHideOperation'].includes(
    validationError.error.operationClass,
  )

export const filterReconciliationSpecErrors = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
): validationError is ValidationErrorWithLocation<
  DependencyValidationError | ApplicationValidationError
> =>
  (isDependencyValidationError(validationError.error) ||
    isApplicationValidationError(validationError.error)) &&
  validationError.error.operationClass === 'ReconciliationSpecCreateOperation'

export const filterEnumDefinitionErrors = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
  configuredEnumNames: string[],
): validationError is ValidationErrorWithLocation<
  ApplicationValidationError | DependencyValidationError
> => {
  const { error } = validationError

  return (
    (isApplicationValidationError(error) ||
      isDependencyValidationError(error)) &&
    configuredEnumNames.some(
      (configuredEnumName) => error.operationName === configuredEnumName,
    )
  )
}

export const filterEnumLiteralErrors = (
  validationError: ValidationErrorWithLocation<ConfigurationValidationError>,
  configuredEnumNames: string[],
): validationError is ValidationErrorWithLocation<
  ApplicationValidationError | DependencyValidationError
> => {
  const { error } = validationError

  return (
    (isApplicationValidationError(error) ||
      isDependencyValidationError(error)) &&
    (error.operationName ?? '').split('.').length > 1 &&
    configuredEnumNames.some((configuredEnumName) =>
      error.operationName?.startsWith(`${configuredEnumName}.`),
    )
  )
}

export const configurationItemsMapToList = <T>(
  configurationItemsMap: Map<string, ConfigurationItemsByLayer<T>>,
): T[] =>
  Array.from(configurationItemsMap.values()).flatMap((blockValues) =>
    Array.from(blockValues.values()).flatMap((layerItems) =>
      Array.from(layerItems.values()),
    ),
  )

export const configurationLayerItemsToList = <T>(
  items: Optional<ConfigurationItemsByLayer<T>>,
): T[] =>
  items
    ? Array.from(items.values()).flatMap((layerItems) =>
        Array.from(layerItems.values()),
      )
    : []

/**
 * Ids of everything an operations list has to render, without duplicates: those coming from
 * the loaded operations plus those only known from an error. Applies both to the
 * configuration items an operations list is grouped by and to the operation keys within one
 * of those items.
 */
export const listIdsWithOperationsOrErrors = (
  idsFromOperations: Optional<string>[],
  errorsById: Optional<Map<string, unknown>>,
): string[] =>
  Array.from(
    new Set(
      [
        ...idsFromOperations,
        ...(errorsById ? Array.from(errorsById.keys()) : []),
      ].filter(notEmpty),
    ),
  )

/**
 * Layer and block an operation link points at. When the operation isn't loaded, the only way
 * its key could have been processed is that at least one error carries it, so the location of
 * that error is where the operation lives.
 */
export const resolveOperationLocation = (
  operation: Optional<{ layer: LayerKey; block: string }>,
  errors: Optional<{ layer: LayerKey; block: string }[]>,
) => ({
  layer: (operation?.layer ?? errors?.at(0)?.layer) as LayerKey,
  block: (operation?.block ?? errors?.at(0)?.block) as string,
})
